/**
 * Aktools 数据源配置卡片组件
 * 用于配置 Aktools base URL
 */

import { useState, useEffect } from 'react';
import { Card } from './Card';
import { Input } from './Input';
import { Button } from './Button';
import { useTranslation } from 'react-i18next';
import { useSettingsStore } from '@/stores/settingsStore';
import { aktoolsService } from '@/services/aktools';

export const AktoolsConfigCard = () => {
  const { t } = useTranslation();
  const { aktoolsBaseUrl, setAktoolsBaseUrl } = useSettingsStore();
  
  const [inputValue, setInputValue] = useState(aktoolsBaseUrl || '');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    valid: boolean;
    message: string;
  } | null>(null);

  // 当存储中的 base URL 变化时，更新输入框
  useEffect(() => {
    setInputValue(aktoolsBaseUrl || '');
  }, [aktoolsBaseUrl]);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      // 测试连接
      const testUrl = inputValue.trim() || aktoolsService.getBaseUrl();
      const originalBaseUrl = aktoolsService.getBaseUrl();
      
      // 临时设置 base URL 进行测试
      aktoolsService.setBaseUrl(testUrl);
      
      try {
        await aktoolsService.getVersion({ timeout: 5000 });
        setTestResult({
          valid: true,
          message: t('settings.apiKeys.configured'),
        });
      } catch (error: any) {
        setTestResult({
          valid: false,
          message: error.message || '连接失败',
        });
      } finally {
        // 恢复原始 base URL（用户配置或 null 表示用内置默认）
        aktoolsService.setBaseUrl(aktoolsBaseUrl ?? null);
      }
    } catch (error: any) {
      setTestResult({
        valid: false,
        message: error.message || '测试失败',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = () => {
    const url = inputValue.trim() || null;
    setAktoolsBaseUrl(url);
    if (url) {
      aktoolsService.setBaseUrl(url);
    } else {
      aktoolsService.setBaseUrl(null); // 恢复为内置默认地址
    }
    setTestResult({
      valid: true,
      message: url ? t('settings.apiKeys.configured') : '已恢复为内置默认地址',
    });
  };

  const handleClear = () => {
    setInputValue('');
    setAktoolsBaseUrl(null);
    aktoolsService.setBaseUrl(null);
    setTestResult(null);
  };

  // 仅用于展示：只显示用户配置的 URL，不显示内置默认地址
  const displayCurrentUrl = () => {
    const url = aktoolsBaseUrl?.trim();
    if (url) return url;
    return null;
  };

  return (
    <Card>
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            {t('settings.apiKeys.aktools.title')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('settings.apiKeys.aktools.description')}
          </p>
        </div>

        <div className="space-y-3">
          <div className="border-b border-gray-200 dark:border-gray-700 pb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Aktools Base URL
                </span>
                <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded">
                  {t('settings.apiKeys.noKeyRequired')}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="留空则使用内置默认地址"
                  className="flex-1"
                />
                <Button
                  onClick={handleTest}
                  disabled={testing}
                  variant="secondary"
                  size="sm"
                >
                  {testing ? t('settings.apiKeys.testing') : t('settings.apiKeys.test')}
                </Button>
                <Button
                  onClick={handleSave}
                  variant="primary"
                  size="sm"
                >
                  {t('settings.apiKeys.save')}
                </Button>
                {inputValue && (
                  <Button
                    onClick={handleClear}
                    variant="ghost"
                    size="sm"
                  >
                    {t('settings.apiKeys.clear')}
                  </Button>
                )}
              </div>
              {testResult && (
                <div
                  className={`text-xs ${
                    testResult.valid
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  {testResult.message}
                </div>
              )}
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {displayCurrentUrl()
                  ? `当前使用: ${displayCurrentUrl()}`
                  : '未配置，使用内置默认地址'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
